import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import swal from "sweetalert";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import {
  cardBody,
  cardHeader,
  cardHeaderTitle,
  cardSurface,
  primaryBtn,
  secondaryBtn,
  tableCardSurface,
  thStyle,
} from "./assessmentStyles";

const IS_SUPPLIER_INSTANCE = process.env.REACT_APP_INSTANCE_TYPE === "supplier";
const SUBMITTABLE_STATUSES = new Set(["ASSIGNED", "IN_PROGRESS"]);

const pickAssignment = (raw) => {
  if (!raw) return null;
  if (raw.assignment) return raw.assignment;
  if (raw.status_key && raw.assessment_id) return raw;
  return null;
};

const pickAssessment = (raw) => {
  if (!raw) return null;
  if (raw.assessment && typeof raw.assessment === "object") {
    return raw.assessment;
  }
  if (raw.kpis || raw.title) return raw;
  return null;
};

/**
 * Identity for a KPI node in a nested response tree. Matches the server-side
 * response identity: `${source}:${kpi_id}:${source_assessment_id or 0}`. Used
 * both to key local answer state and to look up existing responses on load.
 */
const kpiKey = (node) => {
  const src = node?.source?.type || "company";
  const kpiId =
    src === "riu_backend"
      ? node.source?.question_id ?? node.kpi_id
      : node.kpi_id;
  const srcAss = src === "riu_backend" ? node.source?.assessment_id ?? 0 : 0;
  return `${src}:${kpiId}:${srcAss ?? 0}`;
};

const responseKey = (r) =>
  `${r.source || "company"}:${r.kpi_id}:${r.source_assessment_id ?? 0}`;

/**
 * Render a persisted answer as text for the read-only viewer. Answers are
 * stored as JSON on the server (may be a primitive, string, object, or
 * array). Return `null` when the answer is effectively empty, so the caller
 * can show a "not answered" placeholder.
 */
const formatAnswerForDisplay = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Flatten the nested `kpis[]` tree into an in-order list of
 * `{ node, parent, isChild }` records. Preserves the composer's
 * parent-then-children ordering used by the picker + view components.
 */
const walkTree = (kpis) => {
  const out = [];
  for (const parent of kpis || []) {
    out.push({ node: parent, parent: null, isChild: false });
    for (const child of parent.sub_kpis || []) {
      out.push({ node: child, parent, isChild: true });
    }
  }
  return out;
};

/**
 * Return the list of mandatory KPI keys the supplier still needs to answer.
 * Called client-side before the SUBMIT call — the backend re-enforces the
 * same rule as a safety net.
 */
const findUnansweredMandatory = (kpis, answered) => {
  const missing = [];
  for (const { node } of walkTree(kpis)) {
    if (!node.is_mandatory) continue;
    if (!answered.has(kpiKey(node))) {
      missing.push(node.kpi_title || `KPI #${node.kpi_id}`);
    }
  }
  return missing;
};

const TakeAssessment = () => {
  const { assignmentId: assignmentIdRaw } = useParams();
  const assignmentId = useMemo(() => {
    const n = Number(assignmentIdRaw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [assignmentIdRaw]);
  const history = useHistory();
  const location = useLocation();

  const [assessment, setAssessment] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Refs bypass the stale-closure trap. `mountedRef` guards setState after
   * async awaits; `savingKeysRef` guards against a double-fire when the same
   * input is blurred while its previous save is still in flight; `pendingRef`
   * lets the submit handler know there's still work in-flight even if the
   * dirty state has been cleared optimistically.
   */
  const mountedRef = useRef(true);
  const savingKeysRef = useRef(new Set());
  const pendingSavesRef = useRef(new Set());

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!assignmentId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [assessmentRes, responsesRes] = await Promise.all([
        AssessmentService.getByAssignment(assignmentId),
        AssessmentService.listAssignmentResponses(assignmentId),
      ]);
      if (cancelled) return;
      setLoading(false);
      if (assessmentRes?.isSuccess) {
        const raw = assessmentRes?.data?.data ?? null;
        const parsedAssignment = pickAssignment(raw);
        const parsedAssessment = pickAssessment(raw);
        setAssignment(parsedAssignment);
        if (parsedAssessment) {
          setAssessment(parsedAssessment);
        } else if (parsedAssignment?.assessment_id) {
          const fallbackSource = parsedAssignment.company_id
            ? "company"
            : "riu_backend";
          const fallback = await AssessmentService.getAssessment(
            parsedAssignment.assessment_id,
            fallbackSource
          );
          if (!cancelled && fallback?.isSuccess) {
            setAssessment(fallback?.data?.data ?? null);
          }
        }
      }
      if (responsesRes?.isSuccess) {
        const rows = responsesRes?.data?.data ?? [];
        const seeded = {};
        for (const r of rows) {
          seeded[responseKey(r)] = {
            source: r.source,
            kpi_id: r.kpi_id,
            source_assessment_id: r.source_assessment_id ?? null,
            answer: r.answer ?? "",
            dirty: false,
            saving: false,
          };
        }
        setAnswers(seeded);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assignmentId]);

  const kpis = Array.isArray(assessment?.kpis) ? assessment.kpis : [];
  const flat = useMemo(() => walkTree(kpis), [kpis]);

  const answeredSet = useMemo(() => {
    const set = new Set();
    for (const [key, entry] of Object.entries(answers)) {
      const val = entry?.answer;
      const filled =
        val !== undefined &&
        val !== null &&
        (typeof val !== "string" || val.trim() !== "");
      if (filled) set.add(key);
    }
    return set;
  }, [answers]);

  const canEditAnswers =
    IS_SUPPLIER_INSTANCE &&
    assignment &&
    SUBMITTABLE_STATUSES.has(assignment.status_key);

  const alreadySubmitted =
    IS_SUPPLIER_INSTANCE &&
    assignment &&
    !SUBMITTABLE_STATUSES.has(assignment.status_key);

  const setEntry = (key, patch) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  };

  const handleAnswerChange = (node, value) => {
    const key = kpiKey(node);
    const src = node?.source?.type || "company";
    const kpiId =
      src === "riu_backend"
        ? node.source?.question_id ?? node.kpi_id
        : node.kpi_id;
    const srcAss =
      src === "riu_backend" ? node.source?.assessment_id ?? null : null;
    setEntry(key, {
      source: src,
      kpi_id: kpiId,
      source_assessment_id: srcAss,
      answer: value,
      dirty: true,
    });
  };

  /**
   * Fire-and-save for pickers whose change is the final answer. Bypasses
   * the blur path so the save doesn't lose the value to a stale-closure
   * race between state flush and the immediate blur callback.
   */
  const handleAnswerCommit = async (node, value) => {
    const key = kpiKey(node);
    const src = node?.source?.type || "company";
    const kpiId =
      src === "riu_backend"
        ? node.source?.question_id ?? node.kpi_id
        : node.kpi_id;
    const srcAss =
      src === "riu_backend" ? node.source?.assessment_id ?? null : null;
    setEntry(key, {
      source: src,
      kpi_id: kpiId,
      source_assessment_id: srcAss,
      answer: value,
      dirty: true,
    });
    if (savingKeysRef.current.has(key)) return;
    savingKeysRef.current.add(key);
    pendingSavesRef.current.add(key);
    if (mountedRef.current) setEntry(key, { saving: true });
    const res = await AssessmentService.upsertAssignmentResponse(assignmentId, {
      kpi_id: kpiId,
      source: src,
      source_assessment_id: srcAss && srcAss > 0 ? srcAss : undefined,
      answer: value,
    });
    savingKeysRef.current.delete(key);
    pendingSavesRef.current.delete(key);
    if (!mountedRef.current) return;
    setEntry(key, { saving: false });
    if (res?.isSuccess) setEntry(key, { dirty: false });
  };

  /**
   * Guarded against three race conditions: (1) a double-fire when the same
   * input is blurred while its previous save is in flight; (2) a stale
   * closure on `answers` — read the latest snapshot from ref at call-time;
   * (3) unmount — no setState after the component is gone.
   */
  const handleAnswerBlur = async (node) => {
    const key = kpiKey(node);
    if (savingKeysRef.current.has(key)) return;
    const entry = answers[key];
    if (!entry || !entry.dirty) return;
    savingKeysRef.current.add(key);
    pendingSavesRef.current.add(key);
    if (mountedRef.current) setEntry(key, { saving: true });
    const res = await AssessmentService.upsertAssignmentResponse(assignmentId, {
      kpi_id: entry.kpi_id,
      source: entry.source,
      source_assessment_id:
        entry.source_assessment_id && entry.source_assessment_id > 0
          ? entry.source_assessment_id
          : undefined,
      answer: entry.answer,
    });
    savingKeysRef.current.delete(key);
    pendingSavesRef.current.delete(key);
    if (!mountedRef.current) return;
    setEntry(key, { saving: false });
    if (res?.isSuccess) {
      setEntry(key, { dirty: false });
      if (res?.data?.data?.assignment_status_changed) {
        setAssignment((a) =>
          a ? { ...a, status: 2, status_key: "IN_PROGRESS" } : a
        );
      }
    } else {
      const msg =
        res?.errorBody?.message ||
        res?.message ||
        "Could not save this answer.";
      await swal({ title: "Save failed", text: msg, icon: "error" });
    }
  };

  /**
   * Flush every dirty answer before the submit gate. Fired blur handlers may
   * still be in flight; wait them out. Reads dirty state from the freshest
   * closure the render captured.
   */
  const flushPendingSaves = async () => {
    for (const { node } of flat) {
      const key = kpiKey(node);
      if (answers[key]?.dirty) await handleAnswerBlur(node);
    }
    while (savingKeysRef.current.size > 0) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 50));
    }
  };

  const handleSubmit = async () => {
    if (!assignment?.id) return;

    await flushPendingSaves();

    const missing = findUnansweredMandatory(kpis, answeredSet);
    if (missing.length) {
      const preview = missing.slice(0, 8).join("\n• ");
      const overflow = missing.length > 8 ? `\n…and ${missing.length - 8} more` : "";
      await swal({
        title: "Mandatory answers missing",
        text: `Please answer these mandatory KPIs before submitting:\n\n• ${preview}${overflow}`,
        icon: "warning",
      });
      return;
    }

    const confirmed = await swal({
      title: "Submit Assessment?",
      text: "Once submitted you will no longer be able to edit your answers.",
      icon: "warning",
      buttons: ["Cancel", "Submit"],
      dangerMode: false,
    });
    if (!confirmed) return;

    setSubmitting(true);
    const res = await AssessmentService.submitAssignment(assignment.id);
    setSubmitting(false);
    if (res?.isSuccess) {
      await swal({ title: "Submitted", icon: "success" });
      history.goBack();
    } else {
      const msg =
        res?.errorBody?.message ||
        res?.message ||
        "Please try again.";
      await swal({ title: "Submission failed", text: msg, icon: "error" });
    }
  };

  const inputStyle = (disabled) => ({
    width: "100%",
    height: 34,
    padding: "0 10px",
    border: "1px solid var(--rule)",
    borderRadius: 6,
    fontSize: 13,
    color: "var(--ink)",
    background: disabled ? "var(--surface-2)" : "var(--surface)",
  });

  const qualitativeOptions = (node) =>
    (Array.isArray(node?.details) ? node.details : [])
      .filter((d) => d?.option_type === "column")
      .map((d) => String(d?.option ?? ""))
      .filter(Boolean);

  const renderAnswerInput = ({ node, entry, inputDisabled, isChild, parentAnswered }) => {
    const placeholder =
      isChild && !parentAnswered ? "Answer the parent first" : "Enter value";
    if (node?.question_type === "quantitative") {
      return (
        <input
          type="number"
          value={entry?.answer ?? ""}
          onChange={(e) => handleAnswerChange(node, e.target.value)}
          onBlur={() => handleAnswerBlur(node)}
          disabled={inputDisabled}
          placeholder={placeholder}
          style={inputStyle(inputDisabled)}
        />
      );
    }
    if (node?.question_type === "qualitative") {
      const opts = qualitativeOptions(node);
      return (
        <select
          value={entry?.answer ?? ""}
          onChange={(e) => handleAnswerCommit(node, e.target.value)}
          disabled={inputDisabled}
          style={inputStyle(inputDisabled)}
        >
          <option value="">— Select —</option>
          {opts.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        value={entry?.answer ?? ""}
        onChange={(e) => handleAnswerChange(node, e.target.value)}
        onBlur={() => handleAnswerBlur(node)}
        disabled={inputDisabled}
        placeholder={placeholder}
        style={inputStyle(inputDisabled)}
      />
    );
  };

  const renderRow = ({ node, parent, isChild }, idx) => {
    const key = kpiKey(node);
    const entry = answers[key];
    const parentKey = parent ? kpiKey(parent) : null;
    const parentAnswered = parentKey ? answeredSet.has(parentKey) : true;
    const inputDisabled = !canEditAnswers || (isChild && !parentAnswered);
    const displayAnswer = formatAnswerForDisplay(entry?.answer);
    return (
      <tr key={key}>
        <td>{isChild ? "" : idx + 1}</td>
        <td style={{ paddingLeft: isChild ? 32 : undefined }}>
          {isChild ? "↳ " : ""}
          {node.kpi_title || "(unknown KPI)"}
        </td>
        <td>{node.is_mandatory ? "Yes" : "No"}</td>
        <td>
          {canEditAnswers ? (
            <>
              {renderAnswerInput({
                node,
                entry,
                inputDisabled,
                isChild,
                parentAnswered,
              })}
              {entry?.saving ? (
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--ink-4)",
                    marginTop: 2,
                  }}
                >
                  Saving…
                </div>
              ) : entry?.dirty ? (
                <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }}>
                  Unsaved — click outside to save
                </div>
              ) : null}
            </>
          ) : displayAnswer !== null ? (
            <span
              style={{
                fontSize: 13,
                color: "var(--ink)",
                whiteSpace: "pre-wrap",
              }}
            >
              {displayAnswer}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: "var(--ink-4)" }}>
              — not answered —
            </span>
          )}
        </td>
      </tr>
    );
  };

  const totalKpiCount = flat.length;

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle={assessment ? assessment.title : "Assessment"}
    >
      <div style={cardSurface}>
        <div style={cardHeader}>
          <h5 style={cardHeaderTitle}>
            {IS_SUPPLIER_INSTANCE ? "Start Assessment" : "View Assessment"}
            {totalKpiCount > 0
              ? ` — ${totalKpiCount} KPI${totalKpiCount > 1 ? "s" : ""}`
              : ""}
          </h5>
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => history.goBack()}
          >
            Back
          </button>
        </div>
        <div style={tableCardSurface}>
          <div className="supplier-mgmt-table-scroll">
            <table className="supplier-mgmt-table table">
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 50 }}>#</th>
                  <th style={thStyle}>KPI</th>
                  <th style={{ ...thStyle, width: 140 }}>Mandatory</th>
                  <th style={{ ...thStyle, width: 320 }}>
                    {canEditAnswers ? "Value" : "Answer"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      Loading…
                    </td>
                  </tr>
                ) : totalKpiCount > 0 ? (
                  flat.map((entry, idx) => renderRow(entry, idx))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No KPIs in this assessment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(canEditAnswers || alreadySubmitted) && (
        <div style={{ ...cardSurface, marginTop: 18 }}>
          <div style={cardBody}>
            {canEditAnswers ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    ...primaryBtn,
                    background: "var(--accent)",
                    cursor: submitting ? "wait" : "pointer",
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? "Submitting…" : "Submit Assessment"}
                </button>
              </div>
            ) : (
              <div
                style={{
                  textAlign: "right",
                  fontSize: 13,
                  color: "var(--ink-3)",
                }}
              >
                Already {assignment.status_key.toLowerCase()}.
              </div>
            )}
          </div>
        </div>
      )}
    </SupplierManagementShell>
  );
};

export default TakeAssessment;
